import type { Raw } from 'vue';
import type { ShaderMaterial } from 'three';
import type { HipSpineLandmarkApiObject } from '@/api/hip-spine-study/hipSpineLandmark';
import type { XRAY_HIP_SPINE_VIEW_IDS } from '@/api/xrays';
import type { HipSpinePostureApiObject } from '@/api/hip-spine-study/hipSpinePosture';
import type { GroupNode } from '@/lib/threeScene/nodes/group';
import type { StandardNode } from '@/lib/threeScene/updateNode';
import type { AngleLabelPlacement } from '@/views/3d-planner/hipSpine/angles/angleLabels';
import type {
    HipSpineAngleOverlay,
    HipSpineAngleId,
    HipSpineAnnotationCategory,
} from '@/domain/anatomy/hipSpineAngleOverlays';

export type { HipSpineAnnotationCategory };

/** Per-angle-type display configuration. */
export const ANGLE_DISPLAY_CONFIG: Record<
    HipSpineAngleId,
    { ray1Dashed: boolean; ray2Dashed: boolean }
> = {
    'lumbar-lordosis': { ray1Dashed: false, ray2Dashed: false },
    'sacral-slope': { ray1Dashed: false, ray2Dashed: true },
    'pelvic-femoral-1': { ray1Dashed: false, ray2Dashed: false },
    'pelvic-femoral-2': { ray1Dashed: false, ray2Dashed: false },
    'pelvic-incidence': { ray1Dashed: false, ray2Dashed: false },
    'app-tilt': { ray1Dashed: false, ray2Dashed: true },
};
export type HipSpinePostureViewState = {
    readonly posture: (typeof XRAY_HIP_SPINE_VIEW_IDS)[number];
    imageMaterial: Raw<ShaderMaterial>;
    imageSize: { width: number; height: number } | null;
    landmarks: HipSpineLandmarkApiObject[];
    annotationGroups: AnnotationGroup[];
    angleGroups: AnnotationGroup[];
    angles: HipSpineAngleResult[];
};

export type SafeZoneVisibility = 'show' | 'hide';

export type PosturesAndViewStates = {
    postures: HipSpinePostureApiObject[];
    viewStates: Map<string, HipSpinePostureViewState>;
};

export type AnnotationGroup = {
    /** Short label for X-ray overlay label (e.g. 'L3', 'APP', 'SE') */
    label: string;
    /** Full sidebar name (e.g. 'Lumbar Vertebra (L3)') */
    name: string;
    category: HipSpineAnnotationCategory;
    colour: string;
    /** Parent group node — visibility toggles on this propagate to all children */
    sceneGroup: GroupNode;
    /** All child scene nodes (landmarks, lines, labels) parented to sceneGroup */
    children: StandardNode[];
};

export type HipSpineAngleResult = HipSpineAngleOverlay & { visible: boolean };

/** Scene nodes plus the (overlap-resolvable) label placement for one angle overlay. */
export type AngleNodes = {
    children: StandardNode[];
    labelPlacement: AngleLabelPlacement | null;
};
